import { defineStore } from 'pinia';
import axios from 'axios';

import type { AuthResponseDTO, Role, UserDTO } from '@krafta/shared-types';
import { resolveApiBase } from '@shared/api/apiBase';

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  refreshTokenExpiresAt: string | null;
  user: UserDTO | null;
  loading: boolean;
  error: string | null;
}

const AUTH_STORAGE_KEYS = ['krafta_auth', 'krafta_admin_auth'];

const API_BASE = resolveApiBase(import.meta.env.VITE_AUTH_API_URL);
const buildUrl = (path: string) => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  if (!API_BASE) {
    return normalizedPath;
  }
  if (normalizedPath.startsWith(API_BASE)) {
    return normalizedPath;
  }
  return `${API_BASE}${normalizedPath}`;
};

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    token: null,
    refreshToken: null,
    refreshTokenExpiresAt: null,
    user: null,
    loading: false,
    error: null
  }),
  getters: {
    isAuthenticated: (state) => Boolean(state.token && state.user),
    authHeaders: (state): Record<string, string> =>
      state.token
        ? {
            Authorization: `Bearer ${state.token}`
          }
        : {},
    hasRole: (state) => (role: Role) => Boolean(state.user?.roles?.includes(role)),
    hasAnyRole: (state) => (roles: Role[]) =>
      roles.some((role) => state.user?.roles?.includes(role))
  },
  actions: {
    persistSession(payload: AuthResponseDTO) {
      this.token = payload.accessToken;
      this.refreshToken = payload.refreshToken;
      this.refreshTokenExpiresAt = payload.refreshTokenExpiresAt;
      this.user = payload.user;

      window.localStorage.setItem(
        AUTH_STORAGE_KEYS[0],
        JSON.stringify({
          token: payload.accessToken,
          refreshToken: payload.refreshToken,
          refreshTokenExpiresAt: payload.refreshTokenExpiresAt,
          user: payload.user
        })
      );

      AUTH_STORAGE_KEYS.slice(1).forEach((key) => {
        window.localStorage.removeItem(key);
      });
    },
    clearSession() {
      this.token = null;
      this.user = null;
      this.refreshToken = null;
      this.refreshTokenExpiresAt = null;
      this.error = null;
      AUTH_STORAGE_KEYS.forEach((key) => window.localStorage.removeItem(key));
    },
    setSessionFromResponse(payload: AuthResponseDTO) {
      this.persistSession(payload);
    },
    hydrate() {
      for (const key of AUTH_STORAGE_KEYS) {
        const raw = window.localStorage.getItem(key);
        if (!raw) continue;

        try {
          const parsed = JSON.parse(raw) as {
            token: string;
            refreshToken?: string;
            refreshTokenExpiresAt?: string;
            user: UserDTO;
          };
          this.token = parsed.token;
          this.refreshToken = parsed.refreshToken ?? null;
          this.refreshTokenExpiresAt = parsed.refreshTokenExpiresAt ?? null;
          this.user = parsed.user;

          if (key !== AUTH_STORAGE_KEYS[0]) {
            this.persistSession({
              accessToken: parsed.token,
              refreshToken: parsed.refreshToken ?? '',
              refreshTokenExpiresAt: parsed.refreshTokenExpiresAt ?? '',
              user: parsed.user
            } as AuthResponseDTO);
          }
          break;
        } catch {
          window.localStorage.removeItem(key);
        }
      }
    },
    async login(email: string, password: string) {
      this.loading = true;
      this.error = null;
      try {
        const response = await axios.post(buildUrl('/auth/login'), { email, password });
        this.setSessionFromResponse(response.data as AuthResponseDTO);
      } catch (error) {
        this.error =
          axios.isAxiosError(error) && error.response?.data?.message
            ? String(error.response.data.message)
            : 'Ошибка авторизации';
        this.token = null;
        this.user = null;
        this.refreshToken = null;
        this.refreshTokenExpiresAt = null;
      } finally {
        this.loading = false;
      }
    },
    async loginWithQrToken(qrToken: string) {
      this.loading = true;
      this.error = null;
      try {
        const response = await axios.post(buildUrl('/auth/login-qr'), { qrToken });
        this.setSessionFromResponse(response.data as AuthResponseDTO);
      } catch (error) {
        this.error =
          axios.isAxiosError(error) && error.response?.data?.message
            ? String(error.response.data.message)
            : 'Ошибка входа по коду доступа';
        this.token = null;
        this.user = null;
        this.refreshToken = null;
        this.refreshTokenExpiresAt = null;
      } finally {
        this.loading = false;
      }
    },
    async logout() {
      try {
        if (this.refreshToken) {
          await axios.post(buildUrl('/auth/logout'), { refreshToken: this.refreshToken });
        }
      } catch {
        // ignore logout errors
      } finally {
        this.clearSession();
      }
    }
  }
});

export type AuthStore = ReturnType<typeof useAuthStore>;
